import { logger } from "@platform/browser-logger";

import type { Column, ColumnGroup, ColumnMovedEvent } from "ag-grid-community";

/** Set as `context` on a carrier version's top-level column group so a drag can regroup a carrier's versions. */
export interface CarrierLaneContext {
  carrierId: string;
  laneIndex: number;
}

const isCarrierLaneContext = (value: unknown): value is CarrierLaneContext =>
  typeof value === "object" &&
  value !== null &&
  "carrierId" in value &&
  typeof value.carrierId === "string" &&
  "laneIndex" in value &&
  typeof value.laneIndex === "number";

interface DisplayedLane {
  key: string;
  context: CarrierLaneContext | null;
  colIds: string[];
}

interface TaggedLaneGroup {
  context: CarrierLaneContext;
  groupId: string;
}

/** Walks up from a column group to the nearest ancestor tagged with a {@link CarrierLaneContext}. */
const findTaggedLaneGroup = (
  group: ColumnGroup | null,
): TaggedLaneGroup | null => {
  if (!group) return null;
  const candidate: unknown = group
    .getProvidedColumnGroup()
    .getColGroupDef()?.context;
  return isCarrierLaneContext(candidate)
    ? { context: candidate, groupId: group.getGroupId() }
    : findTaggedLaneGroup(group.getParent());
};

/** Groups displayed leaf columns into lanes (by their tagged top-level group); untagged columns each form their own lane. */
const groupColumnsIntoLanes = (columns: Column[]): Map<string, DisplayedLane> =>
  columns.reduce((lanes, col) => {
    const tagged = findTaggedLaneGroup(col.getParent());
    const key = tagged?.groupId ?? col.getColId();
    const lane = lanes.get(key) ?? {
      key,
      context: tagged?.context ?? null,
      colIds: [],
    };
    return new Map(lanes).set(key, {
      ...lane,
      colIds: [...lane.colIds, col.getColId()],
    });
  }, new Map<string, DisplayedLane>());

const carrierIdOf = (lane: DisplayedLane | undefined): string | undefined =>
  lane?.context?.carrierId;

/**
 * Moves a drop position that landed between two versions of one carrier to
 * the nearer edge of that carrier's block, so the carrier being dropped never
 * splits it. The drag event does not say which way the column travelled, so a
 * drop exactly in the middle of an even-sized block goes after it.
 */
const snapToCarrierBoundary = (
  lanes: DisplayedLane[],
  position: number,
): number => {
  const carrierId = carrierIdOf(lanes[position]);
  const dropIsInsideCarrier =
    carrierId !== undefined && carrierId === carrierIdOf(lanes[position - 1]);
  if (!dropIsInsideCarrier) return position;

  const blockStart = lanes.findIndex((lane) => carrierIdOf(lane) === carrierId);
  const blockSize = lanes.filter(
    (lane) => carrierIdOf(lane) === carrierId,
  ).length;
  const lanesBeforeDrop = position - blockStart;
  return lanesBeforeDrop * 2 < blockSize ? blockStart : blockStart + blockSize;
};

/**
 * After a user drag, rebuilds the order so the dragged carrier's versions sit
 * together in `laneIndex` order at the drop point. Dragging one version of a
 * carrier therefore reorders the whole carrier, a drop between that carrier's
 * own versions snaps back into sequence, and a drop between another carrier's
 * versions snaps to the nearer edge of that carrier's block (see
 * {@link snapToCarrierBoundary}).
 *
 * Supplied to PlanEditor as `onUserColumnMoved`, which plan-grid-core runs
 * before it reads the carrier order back with extractCarrierOrderFromEvent.
 * That reader positions a carrier by its first displayed version, so it needs
 * every carrier's versions contiguous to read the drop correctly. Columns are
 * moved through the grid API rather than a columnDefs rebuild, which stacks
 * cell renderers after a drag.
 *
 * Does nothing for columns whose groups carry no {@link CarrierLaneContext}.
 */
export const gatherCarrierLanes = (event: ColumnMovedEvent): void => {
  try {
    if (!event.finished) return;

    const lanes = [
      ...groupColumnsIntoLanes(event.api.getAllDisplayedColumns()).values(),
    ];
    const movedColumn = event.column ?? event.columns?.[0];
    if (!movedColumn) return;
    const movedColId = movedColumn.getColId();
    const movedLane = lanes.find((lane) => lane.colIds.includes(movedColId));
    const carrierId = movedLane?.context?.carrierId;
    if (!movedLane || carrierId === undefined) return;

    const isMovedCarrier = (lane: DisplayedLane): boolean =>
      lane.context?.carrierId === carrierId;
    const byLaneIndex = (a: DisplayedLane, b: DisplayedLane): number =>
      (a.context?.laneIndex ?? 0) - (b.context?.laneIndex ?? 0);
    const movedCarrierLanes = lanes.filter(isMovedCarrier).sort(byLaneIndex);
    const otherLanes = lanes.filter((lane) => !isMovedCarrier(lane));
    const lanesBeforeDrop = lanes
      .slice(0, lanes.indexOf(movedLane))
      .filter((lane) => !isMovedCarrier(lane)).length;
    const insertAt = snapToCarrierBoundary(otherLanes, lanesBeforeDrop);

    const desired = [
      ...otherLanes.slice(0, insertAt),
      ...movedCarrierLanes,
      ...otherLanes.slice(insertAt),
    ];

    const desiredColIds = desired.flatMap((lane) => lane.colIds);
    const currentColIds = lanes.flatMap((lane) => lane.colIds);
    const alreadyGathered = desiredColIds.every(
      (id, idx) => id === currentColIds[idx],
    );
    if (alreadyGathered) return;

    event.api.moveColumns(desiredColIds, 0);
  } catch (error) {
    logger.error(
      "[gatherCarrierLanes] Error gathering carrier lanes after column move:",
      error as Error,
    );
  }
};
